<template>
  <div>
    <SkeletonTable :loading="loading" :first-load="firstLoad" :cols="6" :rows="15"/>
    <v-data-table
      :headers="headers"
      :items="computed_articles"
      :search="search"
      :custom-filter="filterItems"
      :items-per-page="15"
      :footer-props="{
        itemsPerPageOptions: [15, 30, -1],
        itemsPerPageText: $t('table.itemsPerPageText')
      }"
      sort-by="display_date"
      sort-desc
      class="table-cursor"
      :loading="loading"
      :loading-text="$t('table.loading')"
      v-show="!firstLoad"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('search')"
            single-line
            hide-details
          />
          <v-spacer />
          <v-btn
            color="primary"
            dark
            class="mb-2"
            to="/manage/edit/publications/add/article"
            ><v-icon class="mr-2">mdi-plus-thick</v-icon
            >{{ $t("button.newItem") }}</v-btn
          >
        </v-toolbar>
      </template>
      <template v-slot:item.minerva="{ item }">
        <div class="d-flex flex-column align-center justify-center text-center">
          <div class="d-flex align-center">
            <v-tooltip top v-if="item.minerva_sync_status">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  v-bind="attrs"
                  v-on="on"
                  :color="minervaStatusColor(item.minerva_sync_status)"
                  :text-color="minervaStatusTextColor(item.minerva_sync_status)"
                >
                  {{ minervaStatusLabel(item.minerva_sync_status) }}
                </v-chip>
              </template>
              <div class="py-1">
                <span v-if="minervaStatusDescription(item.minerva_sync_status, item)">
                  {{ minervaStatusDescription(item.minerva_sync_status, item) }}
                </span>
                <template v-if="item.minerva_sync_status !== 'wont-sync'">
                  <span v-if="item.minerva_last_checked_at">
                    {{ $t('form.publication.minervaLastCheckedAt') }}:
                    {{ formatMinervaLastCheckedAt(item.minerva_last_checked_at) }}
                  </span>
                  <span v-else>
                    {{ $t('form.publication.minerva.noChecks') }}
                  </span>
                </template>
              </div>
            </v-tooltip>
            <v-btn
              v-if="availableMinervaUrl(item)"
              icon
              small
              class="ml-2"
              :href="availableMinervaUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('form.publication.minerva.summary.available')"
            >
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
          <span
            class="caption mt-1"
            v-if="item.minerva_sync_status === 'missing-data' && minervaMetadataSummary(item, { includeMissingDataDetails: false })"
          >
            {{ minervaMetadataSummary(item, { includeMissingDataDetails: false }) }}
          </span>
        </div>
      </template>
      <template
        v-slot:item.features="{ item }"
      >
        <v-tooltip
          v-if="item.hidden == true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="red"
              v-bind="attrs"
              v-on="on"
            >
              mdi-eye-off
            </v-icon>
          </template>
          {{ $t('table.features.hidden') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-eye-outline
        </v-icon>
        <v-tooltip
          v-if="item.is_external === true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="pink"
              v-bind="attrs"
              v-on="on"
            >
              mdi-alien
            </v-icon>
          </template>
          {{ $t('table.features.isExternal') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-alien-outline
        </v-icon>
        <v-tooltip
          v-if="item.featured === true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="purple"
              v-bind="attrs"
              v-on="on"
            >
              mdi-alert-octagram
            </v-icon>
          </template>
          {{ $t('table.features.isFeatured') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-alert-octagram-outline
        </v-icon>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          text
          class="mr-2"
          color="primary"
          :to="`/manage/edit/publications/edit/${item.id}`"
        >
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-icon color="red" @click="deleteItem(item)">
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
    <v-row>
        <v-col class="text-right">
          <VueJsonToCsv :json-data="enrichedArticles" csv-title="stays">
            <v-btn text color="secondary" class="mr-2" :disabled="enrichedArticles.length < 1">
              <v-icon left>mdi-file-table-box</v-icon>
              <span class="d-none d-md-block">{{ $t('table.exportCSV') }}</span>
              <span class="d-block d-md-none">{{ $t('table.exportCSV_short') }}</span>
            </v-btn>
          </VueJsonToCsv>
        </v-col>
        <v-col>
          <VueJsonToExcel :data="enrichedArticles" name="stays.xls">
            <v-btn text color="secondary" :disabled="enrichedArticles.length < 1">
              <v-icon left>mdi-microsoft-excel</v-icon>
              <span class="d-none d-md-block">{{ $t('table.exportExcel') }}</span>
              <span class="d-block d-md-none">{{ $t('table.exportExcel_short') }}</span>
            </v-btn>
          </VueJsonToExcel>
        </v-col>
      </v-row>
  </div>
</template>

<script>
import axios from "axios";
import VueJsonToCsv from "vue-json-to-csv";
import VueJsonToExcel from "vue-json-excel";
import SkeletonTable from "@/components/reusables/SkeletonTable";
import minervaDisplay from "@/mixins/minervaDisplay";
export default {
  mixins: [minervaDisplay],
  components: { SkeletonTable, VueJsonToCsv, VueJsonToExcel },
  data() {
    return {
      search: "",
      loading: true,
      firstLoad: true,
      articles: []
    };
  },

  computed: {
    headers() {
      return [
        {
          text: this.$t("title.publication"),
          value: "title",
        },
        {
          text: this.$t("form.authors"),
          value: "authors_text",
        },
        {
          text: this.$t("table.date"),
          value: "display_date"
        },
        {
          text: this.$t("form.features"),
          value: "features",
          filterable: false,
          width: "15%",
          sortable: false
        },
        {
          text: this.$t("table.minervaSync"),
          value: "minerva",
          sortable: false,
          width: "12%"
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          width: "15%",
          filterable: false,
          sortable: false
        }
      ];
    },
    computed_articles: function(){
      let computed_articles = this.articles;
      computed_articles.forEach(function(part, i, a) {
        a[i].display_date = part.article.final_publication_date || part.article.online_publication_date || part.article.accepted_date;
      });
      return computed_articles;
    },
    enrichedArticles() {
      return this.computed_articles.filter(item => this.filterItems("", this.search, item)).map(article => {
        return {
          id: article.id,
          title: article.title,
          best_date: article.display_date,
          publication_date: article.article.publication_date,
          online_publication_date: article.article.online_publication_date,
          accepted_date: article.article.accepted_date,
          doi: article.doi,
          link: article.links.publisher,
          feder_acknowledgements: article.article.feder_acknowledgements,
          acknowledgements: article.acknowledgements.replaceAll("\n", " "),
        };
      });
    },
  },
  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/publication/type/article";
      axios
        .get(path)
        .then(response => {
          this.articles = response.data;
          this.loading = false;
          if (this.firstLoad) this.firstLoad = false;
        })
        .catch(error => {
          console.log(error);
        });
    },
    deleteItem(item) {
      if (confirm(this.$t("message.confirm.delete"))) {
        let path = this.$store.state.endpoint + "/publication/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.articles.indexOf(item);
            this.articles.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t("message.correct.deleteItem")
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },
    filterItems(value, search, item) {
      const search_array = search
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .split(",")
        .map(x => x.trim());
      const today = new Date().toISOString().split('T')[0];
      const thisyear = today.split('-')[0];
      const customValue = (
        item.title +
        " " +
        item.authors_text +
        " " +
        ((item.doi !== null && item.doi.trim() !== "") ? ("doi:" + item.doi) : "no-doi") + 
        (item.is_external !== false ? " external" : "") +
        (item.hidden ? " hidden" : "") +
        (item.article.publication_status == "accepted" ? " accepted" : " published") +
        (item.article.year ? " year:" + item.article.year : " no-year") +
        (item.research_areas.length === 0 ? " no-areas" : "") +
        " " + (item.language || "") +
        " " + (item.minerva_sync_option || "") +
        " " + (item.minerva_sync_status || "no-minerva") +
        " " + JSON.stringify(item.minerva_sync_metadata || {}) +
        " " + (item.minerva_last_checked_at || "")
      )
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return (
        customValue != null &&
        search_array != null &&
        typeof customValue === "string" &&
        search_array.every(function(search){ return customValue.indexOf(search) !== -1 })
      );
    },
  }
};
</script>
